import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { EReviewTimeState, type TReviewQueueItem } from '@onesource/schemas';
import { getMediaUrl } from '@onesource/services';
import { DateTime } from 'luxon';
import { FC } from 'react';
import { MdChevronRight } from 'react-icons/md';

import { ReviewStatusChip } from './review-status-chip';
import { ReviewTeacherCell } from './review-teacher-cell';
import { URGENCY_STYLE, urgencyRowSx, type TUrgency } from './review-urgency';

interface ReviewQueueRowProps {
  item: TReviewQueueItem;
  /** Admins see whose queue each submission is in. */
  showReviewer: boolean;
  onOpen: (submissionId: string) => void;
}

const day = (value: Date | string) => DateTime.fromJSDate(new Date(value)).toLocaleString(DateTime.DATE_MED);
const relative = (value: Date | string) => DateTime.fromJSDate(new Date(value)).toRelative() ?? day(value);
/** "18 hr. ago", "in 5 hr." — short enough for one line in the date column. */
const shortRelative = (value: Date | string) =>
  DateTime.fromJSDate(new Date(value)).toRelative({ style: 'short' }) ?? day(value);

/**
 * What happened last, in the row's own words: when it was closed, or — while it
 * waits — how its review deadline stands.
 */
const timeline = (item: TReviewQueueItem): { text: string; urgency: TUrgency | null } => {
  if (item.rejectedAt) {
    return { text: `Rejected ${day(item.rejectedAt)}`, urgency: null };
  }
  if (item.feedbackSubmittedAt) {
    return { text: `Reviewed ${day(item.feedbackSubmittedAt)}`, urgency: null };
  }
  if (item.reviewTime === EReviewTimeState.overdue && item.reviewOverdueAt) {
    return { text: `Overdue · ${shortRelative(item.reviewOverdueAt)}`, urgency: 'overdue' };
  }
  if (item.reviewTime === EReviewTimeState.due_soon && item.reviewOverdueAt) {
    return { text: `Review due ${shortRelative(item.reviewOverdueAt)}`, urgency: 'due_soon' };
  }
  return { text: `Submitted ${relative(item.submittedAt)}`, urgency: null };
};

export const ReviewQueueRow: FC<ReviewQueueRowProps> = ({ item, showReviewer, onOpen }) => {
  const { text, urgency } = timeline(item);
  return (
    <ButtonBase
      onClick={() => onOpen(item.submissionId)}
      sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        px: { xs: 2, md: 2.5 },
        py: 1.75,
        textAlign: 'left',
        '& + &': { borderTop: '1px solid', borderColor: 'divider' },
        transition: 'background-color 120ms ease-out',
        '&:hover': { bgcolor: '#fff7f9' },
        '&:hover .queue-row-chevron': { color: '#ff4d6d', transform: 'translateX(2px)' },
        ...urgencyRowSx(urgency),
      }}
    >
      <Avatar src={item.student.avatar ? getMediaUrl(item.student.avatar) : undefined} sx={{ width: 40, height: 40 }}>
        {item.student.name.charAt(0).toUpperCase()}
      </Avatar>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', rowGap: 0.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
            {item.student.name}
          </Typography>
          <ReviewStatusChip status={item.status} />
          {item.isLate && item.dueAt && (
            <Tooltip title={`Submitted after the due date, ${day(item.dueAt)}`} arrow>
              <Chip
                size="small"
                variant="outlined"
                label="Late"
                sx={{ height: 22, fontSize: '0.72rem', fontWeight: 600 }}
              />
            </Tooltip>
          )}
        </Box>
        <Typography variant="caption" color="text.secondary" noWrap display="block" sx={{ mt: 0.25 }}>
          {[item.formTitle, item.moduleName].filter(Boolean).join(' · ')}
          {item.student.email ? ` · ${item.student.email}` : ''}
        </Typography>
        {/* Phones hide the right column, so the deadline words come here — never colour alone. */}
        {urgency && (
          <Typography
            variant="caption"
            sx={{ display: { xs: 'block', sm: 'none' }, mt: 0.25, fontWeight: 700, color: URGENCY_STYLE[urgency].text }}
          >
            {text}
          </Typography>
        )}
      </Box>

      {showReviewer && <ReviewTeacherCell teacher={item.reviewer} />}

      <Typography
        variant="caption"
        sx={{
          display: { xs: 'none', sm: 'block' },
          width: 150,
          flexShrink: 0,
          textAlign: 'right',
          color: urgency ? URGENCY_STYLE[urgency].text : 'text.secondary',
          fontWeight: urgency ? 700 : 400,
        }}
      >
        {text}
      </Typography>

      <Box
        className="queue-row-chevron"
        sx={{ display: 'flex', color: 'text.disabled', transition: 'color 120ms ease-out, transform 120ms ease-out' }}
      >
        <MdChevronRight size={22} />
      </Box>
    </ButtonBase>
  );
};
